import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { getUserInfo, submitOnboarding } from "../../services/me";
import {
  isPhoneE164,
  sanitizePhoneInput,
  ensureBiometricInited,
  enrollBiometric,
  biometricTypeLabel,
  markSessionUnlocked,
  type BiometricManager,
} from "../../utils";
import { useTelegram } from "../../hooks";
import { v } from "../../utils/runtime";

/**
 * Экран регистрации, который показывается **вместо** основного интерфейса
 * пока `/me` возвращает `is_onboarded === false`.
 *
 * Состоит из трёх шагов:
 *   Шаг 1. Контакты (e-mail + телефон). Имя/фамилия уже пришли от
 *          Telegram при логине, дата рождения не собирается.
 *   Шаг 2. PIN — придумать 4 цифры и подтвердить их.
 *   Шаг 3. Биометрия (Face ID / Touch ID) — только на устройствах, где
 *          Telegram сообщает `isBiometricAvailable = true`. Юзер может
 *          пропустить, тогда останется только PIN.
 *
 * После успешного `POST /me/onboarding` мы инвалидируем `user-info`, гейт
 * ловит `is_onboarded === true` и уступает место `<Outlet />`. Если админ
 * сбросит анкету — юзер снова попадёт на этот же компонент.
 *
 * Анимации построены на `framer-motion`:
 *   • иконка в header «дышит» (scale + slight rotate);
 *   • поля появляются с stagger от 60 до 220 мс;
 *   • переход между шагами — slide + fade через `AnimatePresence`;
 *   • точки PIN пружинят при вводе цифры и «шейкают» на ошибке;
 *   • успех — большая галочка с pop-анимацией перед unmount-ом.
 */

// ---------- helpers ----------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Step1Data {
  email: string;
  /** С маской "+7 999 999 99 99" — при отправке нормализуется на бэке. */
  phoneNumber: string;
}

interface Step1Errors {
  email?: string;
  phoneNumber?: string;
}

/** Валидатор возвращает ключи i18n, а UI сам рендерит переведённый текст. */
function validateStep1(data: Step1Data): Step1Errors {
  const errors: Step1Errors = {};
  if (!data.email.trim()) errors.email = "required";
  else if (!EMAIL_RE.test(data.email.trim())) errors.email = "email_invalid";
  if (!data.phoneNumber.trim()) {
    errors.phoneNumber = "required";
  } else if (!isPhoneE164(data.phoneNumber)) {
    errors.phoneNumber = "phone_format";
  }
  return errors;
}

// ---------- shared motion variants ----------
//
// По просьбе UX'а мы полностью убрали staggered/slide/blur анимации
// появления шагов и элементов. Оставили только плавный cross-fade
// иконки и заголовков в Header (сверху) — это создаёт эффект
// «перетекания» между шагами без резких перерисовок контента.

/**
 * No-op варианты для motion.div, которые остались в разметке (кнопки,
 * поля ввода, ошибки). Держим их «постоянно видимыми», чтобы motion.div
 * не мигал и не резервировал место под анимацию.
 */
const containerVariants: Variants = {
  hidden: { opacity: 1 },
  visible: { opacity: 1 },
  exit: { opacity: 1 },
};

const itemVariants: Variants = {
  hidden: { opacity: 1, y: 0 },
  visible: { opacity: 1, y: 0 },
};

/** Cross-fade между шагами — без slide, без blur. */
const stepSlideVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.22, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.18, ease: "easeIn" },
  },
};

/** Cross-fade для «перетекающих» текстов Header'а (заголовок/подзаголовок). */
const headerCrossFadeVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.28, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.22, ease: "easeIn" },
  },
};

/**
 * Cross-fade иконки с эффектом «превращения»: старая иконка уменьшается
 * и слегка поворачивается на выходе, новая приезжает из уменьшенного/
 * поворочённого состояния — визуально выглядит как «морф» между двумя
 * иконками. Работает для перехода Придумай PIN → Подтверди → Готово.
 */
const headerIconMorphVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.55,
    rotate: -25,
    filter: "blur(4px)",
  },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    filter: "blur(0px)",
    transition: {
      type: "spring",
      stiffness: 280,
      damping: 22,
      mass: 0.6,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.55,
    rotate: 25,
    filter: "blur(4px)",
    transition: { duration: 0.28, ease: [0.4, 0, 0.2, 1] },
  },
};

// ---------- Header (persistent, cross-fade icon + texts) ----------

/**
 * Смысловое состояние Header'а, полностью определяющее что мы рисуем
 * (иконку, заголовок, подзаголовок). Плоский тип + `useMemo` + один
 * общий Header наверху экрана дают нам эффект «перетекания» между
 * шагами: иконка и тексты меняются через AnimatePresence cross-fade,
 * а не появляются заново на каждом шаге.
 */
export type HeaderVisualKind =
  | "logo"
  | "lock-create"
  | "lock-confirm"
  | "face"
  | "touch"
  | "success";

interface HeaderProps {
  kind: HeaderVisualKind;
  title: string;
  subtitle: string;
}

/**
 * Реальный визуал иконки по её kind'у. Логотип Alfin — отдельный SVG,
 * остальные — «чистые» iconify-глифы без плитки/подложки, только
 * лёгкий glow под успехом. Все имеют одинаковый размер бокса — 72×72 —
 * чтобы cross-fade и scale-анимация превращения не двигали заголовок.
 */
function HeaderVisual({ kind }: { kind: HeaderVisualKind }) {
  const theme = useTheme();

  if (kind === "logo") {
    const logoSrc =
      theme.palette.mode === "dark" ? v("/alfin-logo.svg") : v("/alfin-logo-l.svg");
    return (
      <Box
        component="img"
        src={logoSrc}
        alt="Alfin"
        sx={{
          height: 42,
          width: "auto",
          display: "block",
        }}
      />
    );
  }

  // Разные иконки для create и confirm, чтобы cross-fade был визуально
  // разным «шагом»: закрытый замок → открытый (пин подтверждён) →
  // «щит с галочкой» на success. Все — solar/bold-duotone для единого веса.
  const iconMap: Record<Exclude<HeaderVisualKind, "logo">, string> = {
    "lock-create": "solar:lock-password-bold-duotone",
    "lock-confirm": "solar:lock-keyhole-unlocked-bold-duotone",
    face: "solar:face-scan-circle-bold-duotone",
    touch: "solar:fingerprint-bold-duotone",
    success: "solar:shield-check-bold-duotone",
  };
  const iconName = iconMap[kind];
  const isSuccess = kind === "success";

  return (
    <Box
      sx={(t) => ({
        width: 72,
        height: 72,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: isSuccess ? t.palette.success.main : t.palette.primary.main,
        // Мягкий свет под иконкой — единственная деталь фона, чтобы
        // иконка не «висела» на пустом месте, но без явного квадратика.
        filter: isSuccess
          ? `drop-shadow(0 0 22px ${t.palette.success.main}66)`
          : `drop-shadow(0 6px 18px ${t.palette.primary.main}55)`,
      })}
    >
      <Icon icon={iconName} width={64} height={64} />
    </Box>
  );
}

function Header({ kind, title, subtitle }: HeaderProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px",
        textAlign: "center",
        marginBottom: "6px",
        marginTop: "40px",
        width: "100%",
      }}
    >
      {/* Иконка — фиксированный 72×72 слот с morph-эффектом (scale + rotate + blur).
          `mode="wait"` в AnimatePresence: сначала исчезает старая иконка,
          потом появляется новая — эффект «превращения». */}
      <Box
        sx={{
          position: "relative",
          height: 72,
          width: 72,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={kind}
            variants={headerIconMorphVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HeaderVisual kind={kind} />
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Тексты — обёртка на 100% ширины экрана, чтобы абсолютные motion.div
          не сжимались до 0 (иначе слова переносятся по буквам). */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: 34,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={title}
            variants={headerCrossFadeVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "23px",
                fontWeight: 700,
                color: "text.primary",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>
          </motion.div>
        </AnimatePresence>
      </Box>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: 44,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={subtitle}
            variants={headerCrossFadeVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              padding: "0 20px",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "text.secondary",
                maxWidth: 320,
                lineHeight: 1.4,
                textAlign: "center",
              }}
            >
              {subtitle}
            </Typography>
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
}

// ---------- Step progress: "1 · 2" indicator ----------

function StepProgress({ step, total }: { step: number; total: number }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        marginBottom: "18px",
      }}
    >
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => {
        const active = n === step;
        const done = n < step;
        return (
          <motion.div
            key={n}
            layout
            initial={false}
            animate={{
              width: active ? 34 : 8,
              opacity: active ? 1 : done ? 0.9 : 0.35,
            }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            style={{
              height: 8,
              borderRadius: 4,
              background: active || done ? "currentColor" : "currentColor",
            }}
          >
            <Box
              sx={(theme) => ({
                width: "100%",
                height: "100%",
                borderRadius: "4px",
                bgcolor:
                  active || done
                    ? theme.palette.primary.main
                    : theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.16)"
                      : "rgba(0,0,0,0.15)",
              })}
            />
          </motion.div>
        );
      })}
    </Box>
  );
}

// ---------- Step 1: personal data ----------

interface Step1Props {
  initialData: Partial<Step1Data>;
  onNext: (data: Step1Data) => void;
}

function StepPersonalData({ initialData, onNext }: Step1Props) {
  const { tg } = useTelegram();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { t } = useTranslation("onboarding");

  const [data, setData] = useState<Step1Data>({
    email: initialData.email ?? "",
    phoneNumber: initialData.phoneNumber ?? "",
  });
  const [touched, setTouched] = useState<Record<keyof Step1Data, boolean>>({
    email: false,
    phoneNumber: false,
  });

  const errors = useMemo(() => validateStep1(data), [data]);
  const isValid = Object.keys(errors).length === 0;

  const set = (k: keyof Step1Data, v: string) =>
    setData((prev) => ({ ...prev, [k]: v }));
  const blur = (k: keyof Step1Data) =>
    setTouched((prev) => ({ ...prev, [k]: true }));

  /**
   * Правильный «notched» outlined-стиль: сам `<fieldset>` рисует границу,
   * а `<legend>` автоматически прорезает её вокруг лейбла, когда тот
   * поднимается вверх. Это MUI-поведение по умолчанию — нам достаточно
   * не убивать `fieldset` и покрасить его в наши цвета.
   */
  const fieldSx = {
    // Убираем встроенный запас под helperText — тогда пустое поле НЕ
    // резервирует место, а появление ошибки/подсказки увеличивает
    // высоту только в момент, когда текст реально показан.
    marginBottom: 0,
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      bgcolor: "secondary.main",
      transition: "background 0.15s",
      // ВАЖНО: селектор `& fieldset` бьёт глобальный theme.override,
      // который у нас в baseThemeParameters ставит `& fieldset { border: none }`.
      // Явный `borderStyle: solid` возвращает бордер к жизни.
      "& fieldset": {
        border: "1px solid",
        borderColor: (t: { palette: { mode: string } }) =>
          t.palette.mode === "dark"
            ? "rgba(255,255,255,0.28)"
            : "rgba(0,0,0,0.23)",
        transition: "border-color 0.15s, border-width 0.15s",
      },
      "&:hover fieldset": {
        borderColor: (t: { palette: { mode: string } }) =>
          t.palette.mode === "dark"
            ? "rgba(255,255,255,0.42)"
            : "rgba(0,0,0,0.42)",
      },
      "&.Mui-focused fieldset": {
        borderColor: (t: { palette: { primary: { main: string } } }) =>
          t.palette.primary.main,
        borderWidth: "2px",
      },
      "&.Mui-error fieldset": {
        borderColor: (t: { palette: { error: { main: string } } }) =>
          t.palette.error.main,
      },
    },
    "& .MuiInputLabel-outlined": {
      // Небольшой отступ, чтобы прорезь в бордюре выглядела аккуратно.
      "&.MuiInputLabel-shrink": {
        paddingLeft: "2px",
        paddingRight: "2px",
      },
    },
    "& .MuiInputBase-input": {
      padding: "14px 14px",
      fontSize: "15px",
    },
    "& .MuiFormHelperText-root": {
      // Помощник плотно прилегает к полю и не съедает вертикальный
      // ритм в состоянии «всё чисто».
      marginTop: "4px",
      marginLeft: "12px",
      marginBottom: 0,
      minHeight: 0,
      lineHeight: 1.25,
      fontSize: "12px",
    },
    // Прячем стандартную системную иконку календаря в input[type=date] —
    // вместо неё показываем свою solar-иконку через InputAdornment.
    "& input[type='date']::-webkit-calendar-picker-indicator": {
      opacity: 0,
      display: "none",
    },
  };

  // Хелпер: возвращаем текст ошибки, только если поле уже трогали и
  // есть, что показать. Иначе — undefined, чтобы MUI вообще не рендерил
  // элемент помощника и не резервировал под него высоту.
  const helperFor = (k: keyof Step1Errors): string | undefined => {
    if (!touched[k] || !errors[k]) return undefined;
    return t(`errors.${errors[k]}`);
  };

  /**
   * Просим Telegram передать номер юзера. Сама WebApp получить его
   * напрямую не может — Telegram отправляет `contact` в приватный чат
   * с ботом. Наш бэкенд слушает эти апдейты (TelegramBotService) и
   * пишет `user.phone_number`. Здесь после `sent: true` мы просто
   * пуллим `/me` и как только видим обновлённый номер — подставляем
   * его в форму.
   *
   * Триггерится автоматически при первом фокусе на поле телефона,
   * если оно ещё пустое. Юзер может отменить попап и ввести вручную.
   */
  const [tgSharing, setTgSharing] = useState(false);
  // Показываем попап Telegram один раз за монтирование компонента —
  // не спамим, если юзер отменил или уже ввёл номер и снова фокусится.
  const askedTgRef = useRef(false);

  const requestPhoneFromTelegram = useCallback(() => {
    const anyTg = tg as unknown as {
      requestContact?: (
        cb: (
          sent: boolean | string,
          event?: { status?: string; responseUnsafe?: any },
        ) => void,
      ) => void;
    };
    if (typeof anyTg.requestContact !== "function") {
      // Тихо игнорируем — юзер введёт номер вручную, без снэкбара.
      return;
    }
    setTgSharing(true);
    anyTg.requestContact((sent, event) => {
      const ok =
        sent === true ||
        sent === "sent" ||
        event?.status === "sent" ||
        Boolean(event?.responseUnsafe);
      if (!ok) {
        setTgSharing(false);
        return;
      }

      // Оптимистично: бэкенд может уже пришедшим апдейтом обновить
      // телефон за пару секунд. Пуллим /me с шагом ~1.5с, максимум 12с.
      let attempts = 0;
      const timer = setInterval(async () => {
        attempts += 1;
        try {
          const me = await getUserInfo();
          if (me.phone_number) {
            clearInterval(timer);
            setData((prev) => ({
              ...prev,
              phoneNumber: me.phone_number as string,
            }));
            setTouched((prev) => ({ ...prev, phoneNumber: true }));
            queryClient.invalidateQueries(["user-info"]);
            setTgSharing(false);
            enqueueSnackbar(t("toasts.phone_from_tg"), {
              variant: "success",
            });
          }
        } catch {
          /* ignored — попробуем ещё раз */
        }
        if (attempts >= 8) {
          clearInterval(timer);
          setTgSharing(false);
        }
      }, 1500);
    });
  }, [tg, enqueueSnackbar, queryClient]);

  const handlePhoneFocus = useCallback(() => {
    if (askedTgRef.current) return;
    if (data.phoneNumber.trim() !== "") return;
    askedTgRef.current = true;
    requestPhoneFromTelegram();
  }, [data.phoneNumber, requestPhoneFromTelegram]);

  const handleSubmit = () => {
    setTouched({
      email: true,
      phoneNumber: true,
    });
    if (!isValid) return;
    onNext({
      email: data.email.trim(),
      phoneNumber: data.phoneNumber.trim(),
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 420,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "18px",
      }}
    >
      <Box sx={{ display: "grid", gap: "10px" }}>
        <TextField
          label={t("fields.email")}
          type="email"
          value={data.email}
          onChange={(e) => set("email", e.target.value)}
          onBlur={() => blur("email")}
          error={touched.email && Boolean(errors.email)}
          helperText={helperFor("email")}
          autoComplete="email"
          fullWidth
          sx={fieldSx}
        />

        {/* Телефон — без жёсткой маски: юзер сам вводит код страны
            (RU +7, UA +380, KZ +7, DE +49 и т.д.). Санитайзер оставляет
            только `+` в начале и цифры. При фокусе автоматически поднимаем
            попап Telegram «поделиться номером»; юзер может отменить
            и ввести вручную. */}
        <TextField
          label={t("fields.phone")}
          type="tel"
          value={data.phoneNumber}
          onChange={(e) =>
            set("phoneNumber", sanitizePhoneInput(e.target.value))
          }
          onBlur={() => blur("phoneNumber")}
          onFocus={handlePhoneFocus}
          error={touched.phoneNumber && Boolean(errors.phoneNumber)}
          helperText={
            tgSharing ? t("hints.waiting_tg_phone") : helperFor("phoneNumber")
          }
          placeholder={t("fields.phone_placeholder")}
          autoComplete="tel"
          fullWidth
          sx={fieldSx}
          inputProps={{ inputMode: "tel", maxLength: 20 }}
          InputProps={
            tgSharing
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <CircularProgress size={18} />
                    </InputAdornment>
                  ),
                }
              : undefined
          }
        />
      </Box>

      <PrimaryButton onClick={handleSubmit} disabled={!isValid}>
        {t("buttons.next")}
      </PrimaryButton>
    </Box>
  );
}

// ---------- Step 2: PIN ----------

interface Step2Props {
  onBack: () => void;
  onSubmit: (pin: string) => void;
  isLoading: boolean;
  /** Прокидываем текущую фазу наружу, чтобы Header (на уровне
   *  OnboardingScreen) мог показать корректный «Придумайте / Подтвердите». */
  onPhaseChange?: (phase: "create" | "confirm") => void;
}

function StepPin({ onBack, onSubmit, isLoading, onPhaseChange }: Step2Props) {
  const { t } = useTranslation("onboarding");
  const [phase, setPhase] = useState<"create" | "confirm">("create");
  useEffect(() => {
    onPhaseChange?.(phase);
  }, [phase, onPhaseChange]);
  const [firstPin, setFirstPin] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Тригерим шейк на dots при ошибке — увеличиваем счётчик,
  // framer-motion автоматически проигрывает keyframes.
  const [shakeKey, setShakeKey] = useState(0);

  const submittingRef = useRef(false);
  const inputLocked = isLoading || submittingRef.current;

  const handleDigit = (d: string) => {
    if (inputLocked) return;
    setError(null);
    setPin((prev) => (prev.length < 4 ? prev + d : prev));
  };
  const handleDelete = () => {
    if (inputLocked) return;
    setError(null);
    setPin((prev) => prev.slice(0, -1));
  };
  const handleClear = () => {
    if (inputLocked) return;
    setError(null);
    setPin("");
  };

  // Refs, чтобы основной эффект зависел только от `pin`.
  const phaseRef = useRef(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  const firstPinRef = useRef(firstPin);
  useEffect(() => {
    firstPinRef.current = firstPin;
  }, [firstPin]);
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  useEffect(() => {
    if (pin.length !== 4) return;

    if (phaseRef.current === "create") {
      // Небольшой delay, чтобы юзер увидел, что 4-я точка «зажглась»,
      // прежде чем мы переключим фазу.
      const timer = setTimeout(() => {
        setFirstPin(pin);
        setPin("");
        setPhase("confirm");
      }, 220);
      return () => clearTimeout(timer);
    }

    if (pin !== firstPinRef.current) {
      setError(t("errors.pin_mismatch"));
      setShakeKey((k) => k + 1);
      const timer = setTimeout(() => {
        setFirstPin("");
        setPin("");
        setPhase("create");
      }, 380);
      return () => clearTimeout(timer);
    }

    if (submittingRef.current) return;
    submittingRef.current = true;
    // Тот же короткий delay, чтобы 4-я точка успела «пружинить».
    const timer = setTimeout(() => onSubmitRef.current(pin), 180);
    return () => clearTimeout(timer);
  }, [pin]);

  const prevLoadingRef = useRef(isLoading);
  useEffect(() => {
    if (prevLoadingRef.current === true && isLoading === false) {
      submittingRef.current = false;
      setPin("");
      setFirstPin("");
      setPhase("create");
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading]);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 360,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "22px",
      }}
    >
      {/* Точки PIN — с шейком при ошибке и пружиной при вводе цифры */}
      <motion.div
        key={shakeKey}
        animate={
          error
            ? { x: [0, -12, 12, -8, 8, -4, 4, 0] }
            : { x: 0 }
        }
        transition={{ duration: 0.4 }}
        style={{ display: "flex", gap: 18, marginTop: 4 }}
      >
        {[0, 1, 2, 3].map((i) => {
          const filled = i < pin.length;
          return (
            <motion.div
              key={i}
              animate={
                filled
                  ? { scale: [1, 1.35, 1] }
                  : { scale: 1 }
              }
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <Box
                sx={(theme) => ({
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: `2px solid ${
                    error ? theme.palette.error.main : theme.palette.primary.main
                  }`,
                  bgcolor: filled
                    ? error
                      ? theme.palette.error.main
                      : theme.palette.primary.main
                    : "transparent",
                  transition:
                    "background 0.15s, border-color 0.15s, box-shadow 0.2s",
                  boxShadow: filled
                    ? `0 0 12px ${
                        error
                          ? theme.palette.error.main + "66"
                          : theme.palette.primary.main + "66"
                      }`
                    : "none",
                })}
              />
            </motion.div>
          );
        })}
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "error.main",
                textAlign: "center",
                lineHeight: 1.3,
                marginTop: "-8px",
              }}
            >
              {error}
            </Typography>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Клавиатура */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "12px",
          width: "100%",
          maxWidth: 300,
        }}
      >
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <KeypadButton
            key={d}
            onClick={() => handleDigit(d)}
            disabled={isLoading}
          >
            {d}
          </KeypadButton>
        ))}
        <KeypadButton
          onClick={handleClear}
          disabled={isLoading || pin.length === 0}
        >
          <Icon icon="solar:refresh-linear" width={22} height={22} />
        </KeypadButton>
        <KeypadButton onClick={() => handleDigit("0")} disabled={isLoading}>
          0
        </KeypadButton>
        <KeypadButton
          onClick={handleDelete}
          disabled={isLoading || pin.length === 0}
        >
          <Icon icon="solar:backspace-linear" width={22} height={22} />
        </KeypadButton>
      </Box>

      <ButtonBase
        onClick={onBack}
        disabled={isLoading}
        sx={{
          padding: "8px 14px",
          fontSize: "13px",
          color: "text.secondary",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          borderRadius: "12px",
        }}
      >
        <Icon icon="solar:alt-arrow-left-linear" width={16} height={16} />
        {t("buttons.back_step1")}
      </ButtonBase>

      {isLoading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "text.secondary",
            fontSize: "13px",
          }}
        >
          <CircularProgress size={16} />
          {t("buttons.saving")}
        </Box>
      )}
    </Box>
  );
}

// ---------- keypad button ----------

function KeypadButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <motion.div
      whileTap={disabled ? undefined : { scale: 0.9 }}
      transition={{ type: "spring", stiffness: 550, damping: 26 }}
    >
      <ButtonBase
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        sx={(theme) => ({
          width: "100%",
          aspectRatio: "1 / 1",
          borderRadius: "50%",
          fontSize: "24px",
          fontWeight: 500,
          color: "text.primary",
          bgcolor: "secondary.main",
          border:
            theme.palette.mode === "light"
              ? "1px solid rgba(0, 0, 0, 0.05)"
              : "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background 0.15s",
          opacity: disabled ? 0.4 : 1,
          "&:active": {
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(0, 0, 0, 0.04)",
          },
        })}
      >
        {children}
      </ButtonBase>
    </motion.div>
  );
}

// ---------- primary button ----------

function PrimaryButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <motion.div
      whileTap={disabled ? undefined : { scale: 0.98 }}
      transition={{ type: "spring", stiffness: 500, damping: 24 }}
    >
      <ButtonBase
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        sx={{
          width: "100%",
          padding: "14px 20px",
          borderRadius: "40px",
          bgcolor: "primary.main",
          color: "primary.contrastText",
          fontSize: "15px",
          fontWeight: 500,
          opacity: disabled ? 0.5 : 1,
          transition: "opacity 0.15s, box-shadow 0.2s",
          boxShadow: disabled ? "none" : "0 6px 22px rgba(0,0,0,0.18)",
        }}
      >
        {children}
      </ButtonBase>
    </motion.div>
  );
}

// ---------- success burst ----------

// Экран «Готово» рисуется через persistent Header в root'e OnboardingScreen:
// иконка cross-fades с замка на checkmark, тексты — на «Готово!/Открываем
// ваш кошелёк…». Отдельный SuccessBurst-компонент больше не нужен.

// ---------- Step 3: biometric enroll ----------

interface StepBiometricProps {
  pin: string;
  bm: BiometricManager;
  onDone: () => void;
}

/**
 * Показывается только когда `BiometricManager.isBiometricAvailable === true`.
 * Юзер может либо включить Face ID / Touch ID (мы сохраняем PIN в защищённом
 * хранилище устройства), либо пропустить — тогда на lock-screen'e останется
 * только PIN.
 */
function StepBiometric({ pin, bm, onDone }: StepBiometricProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("onboarding");
  const [busy, setBusy] = useState(false);
  const label = biometricTypeLabel(bm.biometricType);

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const ok = await enrollBiometric(pin);
      if (ok) {
        // Сессия уже разблокирована — мы только что задали и подтвердили PIN,
        // не будем сразу гонять юзера через lock-screen.
        markSessionUnlocked();
        enqueueSnackbar(t("toasts.biometric_enrolled", { label }), {
          variant: "success",
        });
      } else {
        enqueueSnackbar(t("toasts.biometric_failed"), {
          variant: "warning",
        });
      }
    } catch {
      enqueueSnackbar(t("toasts.biometric_failed"), { variant: "warning" });
    } finally {
      setBusy(false);
      onDone();
    }
  };

  const handleSkip = () => {
    markSessionUnlocked();
    onDone();
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 420,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "22px",
        alignItems: "center",
      }}
    >
      <PrimaryButton onClick={handleEnable} disabled={busy}>
        {busy ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} sx={{ color: "inherit" }} />
            {t("buttons.enrolling")}
          </Box>
        ) : (
          t("buttons.enable_biometric", { label })
        )}
      </PrimaryButton>

      <ButtonBase
        onClick={handleSkip}
        disabled={busy}
        sx={{
          padding: "8px 14px",
          fontSize: "13px",
          color: "text.secondary",
          borderRadius: "12px",
        }}
      >
        {t("buttons.skip")}
      </ButtonBase>
    </Box>
  );
}

// ---------- root ----------

export function OnboardingScreen() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("onboarding");

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [personal, setPersonal] = useState<Step1Data | null>(null);
  // PIN держим в state ровно до момента, пока не решится вопрос с
  // биометрией (Step 3). Сразу после этого — сбрасываем, чтобы он не
  // висел в памяти дольше необходимого.
  const [confirmedPin, setConfirmedPin] = useState<string | null>(null);
  // Если Telegram сообщил `isBiometricAvailable = true`, на step 3
  // покажем предложение включить Face ID / Touch ID. Иначе пропускаем.
  const [biometric, setBiometric] = useState<BiometricManager | null>(null);
  // Фаза внутри step 2, приходит из StepPin через onPhaseChange.
  // Позволяет persistent Header показывать «Придумайте PIN-код» на первой
  // фазе и «Подтвердите PIN-код» на второй, не перерисовывая иконку.
  const [pinPhase, setPinPhase] = useState<"create" | "confirm">("create");
  // Кратковременный «Готово!» перед тем как гейт нас размонтирует.
  const [succeeded, setSucceeded] = useState(false);

  // Инитим BiometricManager один раз при монтировании — чтобы к моменту,
  // когда юзер закончит вводить PIN, `isBiometricAvailable` уже был
  // прочитан и мы могли сразу решить, показывать ли Step 3.
  useEffect(() => {
    let cancelled = false;
    ensureBiometricInited().then((bm) => {
      if (!cancelled && bm?.isBiometricAvailable) setBiometric(bm);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const finishOnboarding = useCallback(() => {
    setConfirmedPin(null);
    setSucceeded(true);
    // Держим экран «Готово!» видимым >=2 секунд перед тем как инвалидировать
    // /me — иначе гейт сразу размонтирует нас и юзер даже не увидит
    // подтверждение регистрации.
    setTimeout(() => {
      queryClient.invalidateQueries(["user-info"]);
    }, 2200);
    enqueueSnackbar(t("toasts.onboarded"), { variant: "success" });
  }, [enqueueSnackbar, queryClient, t]);

  const mutation = useMutation({
    mutationFn: submitOnboarding,
    onSuccess: () => {
      // Успех сабмита анкеты: если устройство поддерживает биометрию,
      // предлагаем её подключить (step 3). Иначе сразу заканчиваем.
      if (biometric?.isBiometricAvailable) {
        setStep(3);
      } else {
        // Юзер уже онбордингнут — сессия «разблокирована», lock-screen
        // не должен появиться при первом переходе в кабинет.
        markSessionUnlocked();
        finishOnboarding();
      }
    },
    onError: (err) => {
      const anyErr = err as {
        response?: { data?: { message?: string | string[] } };
        message?: string;
      };
      const raw = anyErr?.response?.data?.message ?? anyErr?.message;
      const msg = Array.isArray(raw)
        ? raw.join(", ")
        : raw ?? t("errors.save_failed");
      enqueueSnackbar(msg, { variant: "error" });
    },
  });

  const handleStep1 = useCallback((data: Step1Data) => {
    setPersonal(data);
    setStep(2);
  }, []);

  const handleBackToStep1 = useCallback(() => {
    setStep(1);
  }, []);

  const personalRef = useRef(personal);
  useEffect(() => {
    personalRef.current = personal;
  }, [personal]);

  const mutateRef = useRef(mutation.mutate);
  useEffect(() => {
    mutateRef.current = mutation.mutate;
  }, [mutation.mutate]);

  const handleSubmitPin = useCallback((pin: string) => {
    const p = personalRef.current;
    if (!p) {
      setStep(1);
      return;
    }
    setConfirmedPin(pin);
    mutateRef.current({
      email: p.email,
      phoneNumber: p.phoneNumber,
      pin,
    });
  }, []);

  const totalSteps = biometric?.isBiometricAvailable ? 3 : 2;
  const rootTheme = useTheme();

  // Определяем что показывать в persistent Header'e на основе текущего
  // состояния. Иконка меняется только когда МЕНЯЕТСЯ kind — поэтому переход
  // «Придумайте → Подтвердите PIN» не перерисовывает иконку (замок остаётся
  // на месте), а меняются только заголовок и подзаголовок. А вот переход
  // на success cross-fades замок на checkmark.
  const header: {
    kind: HeaderVisualKind;
    title: string;
    subtitle: string;
  } = (() => {
    if (succeeded) {
      return {
        kind: "success",
        title: t("header.done_title"),
        subtitle: t("header.done_subtitle"),
      };
    }
    if (step === 1) {
      return {
        kind: "logo",
        title: t("header.register"),
        subtitle: t("header.register_subtitle"),
      };
    }
    if (step === 2) {
      return {
        kind: pinPhase === "create" ? "lock-create" : "lock-confirm",
        title:
          pinPhase === "create"
            ? t("header.pin_create")
            : t("header.pin_confirm"),
        subtitle:
          pinPhase === "create"
            ? t("header.pin_create_subtitle")
            : t("header.pin_confirm_subtitle"),
      };
    }
    // step === 3 — биометрия
    const isFace = biometric?.biometricType === "face";
    const kind: HeaderVisualKind = isFace ? "face" : "touch";
    return {
      kind,
      title: isFace ? t("header.biometric_face") : t("header.biometric_touch"),
      subtitle: t("header.biometric_subtitle"),
    };
  })();

  return (
    <>
      {/* Фон-градиент — тот же, что на главной (/home) и /exchange:
          волна + звёздочки в верхней части экрана. Плавно исчезает
          на шаге «Готово!». */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          width: "100%",
          height: "100%",
          maxHeight: "280px",
          backgroundImage:
            rootTheme.palette.mode === "dark"
              ? `url('${v("/dark-theme-gradient.jpg")}')`
              : `url('${v("/light-theme-gradient.jpg")}')`,
          backgroundPosition: "top",
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% auto",
          pointerEvents: "none",
          zIndex: 0,
          opacity: succeeded ? 0 : 1,
          transition: "opacity 700ms ease-out",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "8px 4px 40px",
          overflow: "hidden",
        }}
      >
        {/* Индикатор шагов — плавно скрывается на success. */}
        <Box
          sx={{
            opacity: succeeded ? 0 : 1,
            transition: "opacity 300ms ease-out",
            width: "100%",
          }}
        >
          <StepProgress step={step} total={totalSteps} />
        </Box>

        {/* Persistent Header — иконка, заголовок и подзаголовок «перетекают»
            между шагами через AnimatePresence cross-fade внутри самого
            Header'а. Никаких slide- или blur-анимаций больше нет. */}
        <Header
          kind={header.kind}
          title={header.title}
          subtitle={header.subtitle}
        />

        <Box sx={{ position: "relative", width: "100%", marginTop: "18px" }}>
          <AnimatePresence mode="wait">
            {succeeded ? (
              // На success сам контент шага не нужен: всё уже в Header'e.
              // Просто оставляем пустой блок, чтобы прогресс/фон плавно ушли.
              <motion.div
                key="success"
                variants={stepSlideVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ width: "100%", minHeight: 80 }}
              />
            ) : step === 1 ? (
              <motion.div
                key="step-1"
                variants={stepSlideVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ width: "100%" }}
              >
                <StepPersonalData
                  initialData={personal ?? {}}
                  onNext={handleStep1}
                />
              </motion.div>
            ) : step === 2 ? (
              <motion.div
                key="step-2"
                variants={stepSlideVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{ width: "100%" }}
              >
                <StepPin
                  onBack={handleBackToStep1}
                  onSubmit={handleSubmitPin}
                  onPhaseChange={setPinPhase}
                  isLoading={mutation.isLoading}
                />
              </motion.div>
            ) : (
              biometric &&
              confirmedPin && (
                <motion.div
                  key="step-3"
                  variants={stepSlideVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ width: "100%" }}
                >
                  <StepBiometric
                    pin={confirmedPin}
                    bm={biometric}
                    onDone={finishOnboarding}
                  />
                </motion.div>
              )
            )}
          </AnimatePresence>
        </Box>
      </Box>
    </>
  );
}
